<template>
  <div class="modern-color-theme font-poppins flex flex-col gap-2" data-component-name="VInput">
    <VSLabel v-if="props.label" :tooltip="props.labelTooltip" :for="id">{{ props.label }}</VSLabel>
    <div
      ref="internal-component-ref"
      class="flex items-center focus-within:shadow-blue-600 overflow-hidden"
      :class="computedWrapperClass"
    >
      <div v-if="slots.prefix" class="pl-2">
        <slot name="prefix" />
      </div>
      <input
        :id="id"
        ref="focusElement"
        v-model="modelValueInternal"
        :type="props.type"
        :disabled="props.disabled"
        :autocomplete="props.autocomplete"
        :readonly="props.readonly"
        :maxlength="props.maxLength"
        :placeholder="props.placeholder"
        :name="props.name"
        class="w-full rounded-md m-[2px] pl-2.5 !border-none focus:ring-0 shadow-none py-[calc(0.375rem-2px)] placeholder:text-neutral-500 text-sm leading-6 bg-inherit"
        :class="computedInputClass"
        :aria-describedby="descriptionId"
        @keydown="(event) => { handleKeyDown(event); scheduleFlush(); }"
        @paste="handlePaste"
        @keydown.enter="flush"
        @focusin="handleFocus"
        @blur="(event) => { handleFocus(event); flush(); }"
      >
      <VSResetButton v-if="modelValueInternal" container="ml-auto pr-2 flex" v-bind="props" @click="reset" />
      <div v-if="props.maxLength" class="pr-4 whitespace-nowrap text-xs leading-4 font-normal" :class="{ 'text-neutral-500': !props.disabled, 'text-neutral-250': props.disabled }" role="status" aria-live="polite">
        {{ modelValueInternalLength }} / {{ props.maxLength }}
      </div>
      <div v-if="slots.suffix" class="pr-2 flex items-center gap-2">
        <slot name="suffix" />
      </div>
    </div>
    <VSValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" :position="props.validationPosition" :anchor="internalComponent" :anchor-focused="isFocused" />
    <VSDescription v-if="props.description" :id="descriptionId" v-safe-html="props.description" />
    <slot name="teleport" />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue';
import VSDescription from './components/VSDescription.vue'
import VSLabel from './components/VSLabel.vue'
import VSValidation from './components/VSValidation.vue';
import { useElementId, useDescriptionElementId } from '../../utils/utils';
import { useFocus } from '@utils/focus';
import { useBuffering } from './helpers/buffering';
import { handleEvents } from './helpers/input';
import { type PlaceholderProps, SHARED_PROPS_DEFAULTS, VALIDATION_PROPS_DEFAULTS, type AutocompleteProps, type LengthRestrictionProps, type SharedProps, type ValidationProps, type BufferingProps } from './helpers/types';
import { createDefaultValidator, useValidation } from './helpers/validations';
import VSResetButton from './components/VSResetButton.vue';
import { useTheme } from '@library/themes/themes';
import type { ThemeProp } from '@library/themes/types';

defineOptions({
  name: 'VInput'
})

const slots = defineSlots()

const props = withDefaults(
  defineProps<SharedProps & ValidationProps<string> & AutocompleteProps & LengthRestrictionProps & PlaceholderProps & BufferingProps & {
    type?: 'text' | 'password' | 'email' | 'search',
    variant?: 'default' | 'underlined',
    focus?: boolean
    centered?: boolean
    required?: boolean
    whitelist?: string[]
    allowEmpty?: boolean
    trim?: boolean
    theme?: ThemeProp<'VInput'>
  }>(),
  {
    ...SHARED_PROPS_DEFAULTS,
    ...VALIDATION_PROPS_DEFAULTS,
    type: 'text',
    variant: 'default',
    focus: false,
    centered: false,
    required: false,
    placeholder: undefined,
    whitelist: undefined,
    trim: true,
    allowEmpty: false,
    autocomplete: 'off',
    theme: undefined
  }
)

const id = useElementId(props.id)
const descriptionId = useDescriptionElementId(props.description, id.value)

const modelValue = defineModel<undefined | null | string>({ required: true })

const isFocused = ref(false)

const flush = () => {
  if (props.trim) {
    modelValueInternal.value = modelValueInternal.value?.trim()
  }

  flushInternal()
}

const { intermediateModel: modelValueInternal, scheduleFlush, flush: flushInternal } = useBuffering(modelValue, props)

const modelValueInternalLength = computed(() => modelValueInternal.value?.length ?? 0)

const { validationVisible, validationResult, isValid, isValidationError, isValidationWarning } = useValidation(
  modelValueInternal,
  props,
  createDefaultValidator(props)
)

const reset = () => {
  modelValueInternal.value = null

  scheduleFlush()
}

const theme = useTheme(props, 'VInput')

const computedInputClass = computed(() => [
  ...(props.disabled ? [theme.value.content.disabled] : []),
  ...(!props.disabled && props.readonly ? [theme.value.content.readonly] : []),
  ...(!props.disabled && !props.readonly ? [theme.value.content.enabled] : []),
  {
    'text-center': props.centered,
    '[&::-webkit-search-cancel-button]:hidden': props.type === 'search'
  }
])

const computedWrapperClass = computed(() => [
  ...(props.variant === 'default' ? [
    ...(props.disabled ? [theme.value.wrapper.default.disabled] : []),
    ...(!props.disabled && props.readonly ? [theme.value.wrapper.default.readonly] : []),
    ...(!props.disabled && !props.readonly ? [theme.value.wrapper.default.enabled] : []),
    'rounded-md shadow-[inset_0_0_0_1px] focus-within:shadow-[inset_0_0_0_2px]'
  ] : []),
  ...(props.variant === 'underlined' ? [
    ...(props.disabled ? [theme.value.wrapper.underlined.disabled] : []),
    ...(!props.disabled && props.readonly ? [theme.value.wrapper.underlined.readonly] : []),
    ...(!props.disabled && !props.readonly ? [theme.value.wrapper.underlined.enabled] : []),
    'shadow-[inset_0_-1px_0_0] focus-within:shadow-[inset_0_-2px_0_0]'
  ] : []),
  {
    'shadow-red-300': isValidationError.value,
    'shadow-yellow-300': isValidationWarning.value
  }
])

const { focus, focusElement } = useFocus(props.focus)

const { handleKeyDown, handlePaste, handleFocus } = handleEvents(props, validationVisible, isFocused)

const internalComponent = useTemplateRef('internal-component-ref')

defineExpose({
  focus,
  internalComponent,
  isValid,
  get isEmpty () {
    return !modelValueInternal.value
  }
})
</script>